import React, { FC, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import DatePicker from '@mui/lab/DatePicker';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import { DateTime } from 'luxon';
import { TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AdapterDateLuxon } from '../../../utils';

interface FormDatePickerProps {
  name: string;
  label?: string;
  minDate?: DateTime;
  maxDate?: DateTime;
  handleChange?: (date: DateTime) => void;
}

const defaultProps = {
  label: undefined,
  minDate: undefined,
  maxDate: undefined,
  handleChange: undefined,
};

export const FormDatePicker: FC<FormDatePickerProps> = ({ name, label, minDate, maxDate, handleChange }) => {
  const [open, setOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const { control } = useFormContext();
  const locale = i18n.language === 'ua' ? 'uk' : i18n.language;
  const weekStartsOn = i18n.language === 'en' ? 7 : 1;
  const validationRules = {
    required: {
      value: true,
      message: t('validation.thisFieldIsRequired'),
    },
  };

  const handlePreventPaste = (e) => {
    e.preventDefault();
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={validationRules}
      render={({ field: { onChange, value } }) => {
        return (
          <LocalizationProvider dateAdapter={AdapterDateLuxon(weekStartsOn)} locale={locale}>
            <DatePicker
              open={open}
              onOpen={() => setOpen(true)}
              onClose={() => setOpen(false)}
              inputFormat="dd.MM.yyyy"
              toolbarTitle=""
              label={label}
              value={value}
              views={['day']}
              minDate={minDate}
              maxDate={maxDate}
              disableHighlightToday
              okText=""
              cancelText={t('buttons.close')}
              PaperProps={{ className: 'custom-date-picker' }}
              DialogProps={{ classes: { paper: 'custom-date-paper' } }}
              onChange={(newValue) => {
                onChange(newValue);
                if (handleChange) handleChange(newValue);
              }}
              renderInput={(inputProps) => (
                <TextField
                  className="input input__text-field__primary"
                  onKeyDown={handlePreventPaste}
                  onPaste={handlePreventPaste}
                  onClick={() => setOpen(true)}
                  {...inputProps}
                />
              )}
            />
          </LocalizationProvider>
        );
      }}
    />
  );
};

FormDatePicker.defaultProps = defaultProps;
